import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Auth API
export const authAPI = {
  login: (credentials) => axios.post('/api/auth/login', credentials, { withCredentials: true }),
  logout: () => axios.post('/api/auth/logout', {}, { withCredentials: true }),
  checkAuth: () => axios.get('/api/auth/check', { withCredentials: true }),
  getAzureConfig: () => axios.get('/api/auth/azure/config', { withCredentials: true }),
  getAzureLoginUrl: () => axios.get('/api/auth/login/azure', { withCredentials: true }),
  impersonate: (userId) => axios.post('/api/auth/impersonate', { userId }, { withCredentials: true }),
  stopImpersonate: () => axios.post('/api/auth/stop-impersonate', {}, { withCredentials: true }),
};

// Settings API
export const settingsAPI = {
  // Roles
  getRoles: () => axios.get('/api/settings/roles', { withCredentials: true }),

  // Users
  getUsers: () => axios.get('/api/settings/users', { withCredentials: true }),
  addUser: (userData) => axios.post('/api/settings/users', userData, { withCredentials: true }),
  updateUserRole: (userId, role) => axios.put(`/api/settings/users/${userId}/role`, { role }, { withCredentials: true }),
  updateUserPassword: (userId, password) => axios.put(`/api/settings/users/${userId}/password`, { password }, { withCredentials: true }),
  deleteUser: (userId) => axios.delete(`/api/settings/users/${userId}`, { withCredentials: true }),
  resendInvite: (userId) => axios.post(`/api/settings/users/${userId}/resend-invite`, {}, { withCredentials: true }),
  setUserSuspended: (userId, suspended) => axios.put(`/api/settings/users/${userId}/suspend`, { suspended }, { withCredentials: true }),

  // Role → screen/tab access matrix
  getRolePermissions: () => axios.get('/api/settings/role-permissions', { withCredentials: true }),
  saveRolePermissions: (matrix) => axios.put('/api/settings/role-permissions', { matrix }, { withCredentials: true }),

  // Azure AD Sync
  getAzureSyncStatus: () => axios.get('/api/settings/azure-sync-status', { withCredentials: true }),
  syncAzureUsers: () => axios.post('/api/settings/users/sync-azure', {}, { withCredentials: true }),

  // Email Log
  getEmailLog: (page = 1, pageSize = 25) => axios.get(`/api/settings/email-log?page=${page}&pageSize=${pageSize}`, { withCredentials: true }),

  // Feedback Log
  getFeedbackLog: (page = 1, pageSize = 25) => axios.get(`/api/settings/feedback-log?page=${page}&pageSize=${pageSize}`, { withCredentials: true }),

  // Audit Log
  getAuditLog: (params = {}) => {
    const query = new URLSearchParams({
      page: params.page || 1,
      pageSize: params.pageSize || 25,
      ...(params.search && { search: params.search }),
      ...(params.action && { action: params.action }),
      ...(params.sortBy && { sortBy: params.sortBy }),
      ...(params.sortDir && { sortDir: params.sortDir }),
    }).toString();
    return axios.get(`/api/settings/audit-log?${query}`, { withCredentials: true });
  },

  // Database
  getDbStatus: () => axios.get('/api/settings/db-status', { withCredentials: true }),

  // Versions
  getVersions: () => axios.get('/api/settings/versions', { withCredentials: true }),
  addVersion: (data) => axios.post('/api/settings/versions', data, { withCredentials: true }),
  deleteVersion: (id) => axios.delete(`/api/settings/versions/${id}`, { withCredentials: true }),
};

// Wayleave Documents API
export const wayleaveDocsAPI = {
  getAccess: () => axios.get('/api/wayleave-docs/access', { withCredentials: true }),
  getPacks: () => axios.get('/api/wayleave-docs/packs', { withCredentials: true }),
  getPack: (id) => axios.get(`/api/wayleave-docs/packs/${id}`, { withCredentials: true }),
  createPack: (payload) => axios.post('/api/wayleave-docs/packs', payload, { withCredentials: true }),
  updatePack: (id, payload) => axios.put(`/api/wayleave-docs/packs/${id}`, payload, { withCredentials: true }),
  deletePack: (id) => axios.delete(`/api/wayleave-docs/packs/${id}`, { withCredentials: true }),
  // Reuse the existing wayleave station list for the "new pack" station picker
  getWayleaveStations: () => axios.get('/api/locations/wayleave/all', { withCredentials: true }),
  // Appointment letters (Setup tab)
  getAppointments: () => axios.get('/api/wayleave-docs/appointments', { withCredentials: true }),
  createAppointment: (formData) => axios.post('/api/wayleave-docs/appointments', formData, {
    withCredentials: true,
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  deleteAppointment: (id) => axios.delete(`/api/wayleave-docs/appointments/${id}`, { withCredentials: true }),
  // Contractor-role users (for pack assignment)
  getContractors: () => axios.get('/api/wayleave-docs/contractors', { withCredentials: true }),
  // Consultant (admin/sysadmin) users — pack owner assignment
  getConsultants: () => axios.get('/api/wayleave-docs/consultants', { withCredentials: true }),
  // Document drawer (per-slot review workflow)
  getSlot: (slotId) => axios.get(`/api/wayleave-docs/slots/${slotId}`, { withCredentials: true }),
  uploadRevision: (slotId, formData) => axios.post(`/api/wayleave-docs/slots/${slotId}/revisions`, formData, {
    withCredentials: true,
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  reviewSlot: (slotId, action) => axios.put(`/api/wayleave-docs/slots/${slotId}/review`, { action }, { withCredentials: true }),
  setSlotNotApplicable: (slotId, applicable) => axios.put(`/api/wayleave-docs/slots/${slotId}/not-applicable`, { applicable }, { withCredentials: true }),
  saveContactDetails: (slotId, fields) => axios.put(`/api/wayleave-docs/slots/${slotId}/contact-details`, fields, { withCredentials: true }),
  createChangeRequest: (slotId, payload) => {
    const files = payload.files || [];
    if (files.length) {
      const fd = new FormData();
      if (payload.kind) fd.append('kind', payload.kind);
      fd.append('body', payload.body || '');
      files.forEach((f) => fd.append('files', f));
      return axios.post(`/api/wayleave-docs/slots/${slotId}/change-requests`, fd, { withCredentials: true });
    }
    return axios.post(`/api/wayleave-docs/slots/${slotId}/change-requests`, { kind: payload.kind, body: payload.body }, { withCredentials: true });
  },
  updateChangeRequest: (crId, state) => axios.put(`/api/wayleave-docs/change-requests/${crId}`, { state }, { withCredentials: true }),
  addChangeRequestReply: (crId, body, files = []) => {
    if (files.length) {
      const fd = new FormData();
      fd.append('body', body || '');
      files.forEach((f) => fd.append('files', f));
      return axios.post(`/api/wayleave-docs/change-requests/${crId}/replies`, fd, { withCredentials: true });
    }
    return axios.post(`/api/wayleave-docs/change-requests/${crId}/replies`, { body }, { withCredentials: true });
  },
  // Action queue (My Actions)
  getQueue: () => axios.get('/api/wayleave-docs/queue', { withCredentials: true }),
  // Pack activity timeline
  getPackActivity: (id) => axios.get(`/api/wayleave-docs/packs/${id}/activity`, { withCredentials: true }),
  // DRE submission + decision
  submitPack: (id, payload = {}) => axios.post(`/api/wayleave-docs/packs/${id}/submit`, payload, { withCredentials: true }),
  getSubmissions: () => axios.get('/api/wayleave-docs/submissions', { withCredentials: true }),
  decideSubmission: (submissionId, payload) => axios.put(`/api/wayleave-docs/submissions/${submissionId}/outcome`, payload, { withCredentials: true }),
  // DRE uploads (or replaces) the signed approval letter for an approved submission.
  uploadApprovalLetter: (submissionId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return axios.post(`/api/wayleave-docs/submissions/${submissionId}/approval-letter`, fd, { withCredentials: true });
  },
  // Download every uploaded document for a pack as a single ZIP.
  downloadPackDocuments: async (packId, stationNo) => {
    try {
      const res = await axios.get(`/api/wayleave-docs/packs/${packId}/documents.zip`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Station-${stationNo || packId}-documents.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      // The error body is a Blob (responseType: 'blob'); try to read the JSON message out of it.
      const data = err.response?.data;
      if (data instanceof Blob) {
        try {
          const text = await data.text();
          const parsed = JSON.parse(text);
          if (parsed?.error) throw new Error(parsed.error);
        } catch (parseErr) {
          if (parseErr instanceof Error && parseErr.message && !(parseErr instanceof SyntaxError)) throw parseErr;
        }
      }
      throw err;
    }
  },
};

// SharePoint archive API
export const sharepointAPI = {
  getConnection: () => axios.get('/api/sharepoint/connection', { withCredentials: true }),
  resolveSite: (siteUrl) => axios.post('/api/sharepoint/connection/resolve', { siteUrl }, { withCredentials: true }),
  saveConnection: (payload) => axios.post('/api/sharepoint/connection', payload, { withCredentials: true }),
  testConnection: () => axios.post('/api/sharepoint/connection/test', {}, { withCredentials: true }),
  setDreFolder: (itemId, path) => axios.put('/api/sharepoint/connection/dre-folder', { itemId, path }, { withCredentials: true }),
  browse: (itemId) => axios.get('/api/sharepoint/browse', { params: itemId ? { itemId } : {}, withCredentials: true }),
  getPackSharePoint: (packId) => axios.get(`/api/sharepoint/packs/${packId}/sharepoint`, { withCredentials: true }),
  setPackFolder: (packId, folder) => axios.put(`/api/sharepoint/packs/${packId}/sharepoint/folder`, folder, { withCredentials: true }),
  copy: (packId, fileKeys, dreDest) => axios.post(`/api/sharepoint/packs/${packId}/sharepoint/copy`, { fileKeys, dreDest }, { withCredentials: true }),
};

// Tables API
export const tablesAPI = {
  getTables: () => api.get('/tables'),
  getSchema: (tableName) => api.get(`/tables/${tableName}/schema`),
  getData: (tableName, params) => api.get(`/tables/${tableName}/data`, { params }),
  getRecord: (tableName, id) => api.get(`/tables/${tableName}/data/${id}`),
  createRecord: (tableName, data) => api.post(`/tables/${tableName}/data`, data),
  updateRecord: (tableName, id, data) => api.put(`/tables/${tableName}/data/${id}`, data),
  deleteRecord: (tableName, id) => api.delete(`/tables/${tableName}/data/${id}`),
};

// Password Reset API (unauthenticated)
export const passwordResetAPI = {
  forgotPassword: (email) => axios.post('/api/auth/forgot-password', { email }),
  verifyResetToken: (token) => axios.post('/api/auth/verify-reset-token', { token }),
  resetPassword: (token, password) => axios.post('/api/auth/reset-password', { token, password }),
  confirmOtp: (token, otp) => axios.post('/api/auth/confirm-otp', { token, otp }),
};

// App version (all authenticated users)
export const getAppVersion = () => axios.get('/api/app-version', { withCredentials: true });

// Notifications API
export const notificationsAPI = {
  getAll: () => axios.get('/api/notifications', { withCredentials: true }),
  getUnreadCount: () => axios.get('/api/notifications/unread-count', { withCredentials: true }),
  markRead: (id) => axios.put(`/api/notifications/${id}/read`, {}, { withCredentials: true }),
  markAllRead: () => axios.put('/api/notifications/read-all', {}, { withCredentials: true }),
};

export default api;
